import {Button, Detail, Icon, Label, tokensForThemeProvider} from '@forecast-it/design-system';
import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {useNovaCreditsBlock} from './novaCredits';
import {formatCreditsLong} from './novaCreditsCopy';
import {readBannerDismissed, writeBannerDismissed} from './novaBannerDismissal';
import {isNovaInvocationEnabled} from './novaInvocationGate';
import {getRunningCount, RUN_CAP, subscribeToAnyRunStateChange} from './novaInvocationStore';

export interface NovaEntryBannerProps {
	/**
	 * Whether this person may act on at least one task on the screen. A screen of tasks none of
	 * them can act on gets no banner: it would advertise a control that is nowhere on the page.
	 */
	canEditTasks: boolean;
}

// The grey of the message beside it. The icon palette has no muted fill, so the dimmed sparkle takes
// the auxiliary text colour the message is already drawn in.
const DIMMED_ICON = tokensForThemeProvider?.sky?.sys?.color?.text?.content?.auxiliary ?? '';

// Sticky to the left edge as a guard, not because any screen needs it today. On every entry screen
// the banner sits above whatever scrolls sideways: Task board and Sprints scroll their columns
// inside the drag-and-drop container's own scrollbar, below the banner, so the banner's container
// never moves. A host that did scroll it sideways would otherwise carry the text off screen.
const Banner = styled.div`
	position: sticky;
	left: 0;
	display: flex;
	align-items: center;
	width: 100%;
	box-sizing: border-box;
	gap: ${({theme}) => theme?.sys?.spacing?.['300'] ?? '12px'};
	padding: ${({theme}) => theme?.sys?.spacing?.['300'] ?? '12px'};
	padding-left: ${({theme}) => theme?.sys?.spacing?.['400'] ?? '16px'};
	margin-bottom: ${({theme}) => theme?.sys?.spacing?.['300'] ?? '12px'};
	border: 1px solid ${({theme}) => theme?.ref?.color?.blue?.['100'] ?? ''};
	border-radius: ${({theme}) => theme?.sys?.size?.borderradius?.['3'] ?? '8px'};
	background: ${({theme}) => theme?.ref?.color?.blue?.['50'] ?? ''};
`;

// The heading and the message sit 2px apart. That step is below the theme's spacing scale, whose
// smallest non-zero step is 4px, so it is written out here.
const Lines = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1;
	min-width: 0;
`;

// A copy of a row control, not a control. It is in the sentence so a reader learns what to look
// for on the rows below, and pressing it would do nothing to any particular task - so it takes no
// pointer, no tab stop, and is announced by the one name the wrapper carries rather than by the
// button inside it.
const RefSlot = styled.span`
	display: inline-flex;
	vertical-align: middle;
	margin: 0 2px;
	pointer-events: none;

	button {
		height: 20px;
		min-width: 0;
		padding: 0 6px;
		gap: 3px;
		font-size: 11px;
		line-height: 14px;
	}

	button svg {
		width: 14px;
		height: 14px;
	}
`;

const ChevronRefSlot = styled(RefSlot)`
	button {
		width: 20px;
		min-width: 20px;
		padding: 0;
		justify-content: center;
	}
`;

const DismissSlot = styled.span`
	flex: none;

	button {
		height: 28px;
		width: 28px;
		min-width: 28px;
		padding: 0;
		justify-content: center;
	}
`;

const ControlRef = ({kind, label}: {kind: 'action' | 'chevron'; label: string}): JSX.Element => {
	const Slot = kind === 'chevron' ? ChevronRefSlot : RefSlot;
	return (
		<Slot role="img" aria-label={label} data-cy={`nova-banner-ref-${kind}`}>
			<Button
				emphasis="medium"
				leftIcon={kind === 'chevron' ? 'chevronDown' : 'sparkle'}
				useButtonProps
				noMargin
				tabIndex={-1}
				aria-hidden="true"
				onPress={() => undefined}
			>
				{kind === 'chevron' ? undefined : label}
			</Button>
		</Slot>
	);
};

/**
 * The one place on a task screen that says what Nova is for.
 *
 * Its state follows the runs as they start and finish, so a person who presses Action watches the
 * same banner report what they just did rather than keeping a stale invitation on screen.
 *
 * Where more than one state applies, the first of these wins: out of credits, the run limit, runs
 * in progress, nothing running. The two blocked states outrank the others because a person who
 * cannot start a run needs the reason more than the invitation, and they are the two the banner
 * refuses to be dismissed in.
 */
export const NovaEntryBanner = (props: NovaEntryBannerProps): JSX.Element | null => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const enabled = isNovaInvocationEnabled();
	const [dismissed, setDismissed] = useState(readBannerDismissed);
	const [running, setRunning] = useState(() => (enabled ? getRunningCount() : 0));
	const credits = useNovaCreditsBlock(enabled);

	useEffect(() => {
		if (!enabled) return undefined;
		// Read on subscribe as well: a run can start between this component's first render and the
		// effect running, and that write dispatches to nobody yet.
		setRunning(getRunningCount());
		return subscribeToAnyRunStateChange(() => setRunning(getRunningCount()));
	}, [enabled]);

	if (!enabled || !props.canEditTasks) return null;

	const outOfCredits = credits.blocked;
	const atRunCap = running >= RUN_CAP;
	// A blocked banner cannot be closed, and shows even to someone who closed it before. The
	// reason is the only thing on the screen that explains why every Nova control has gone quiet,
	// so it is not the person's to hide - and when the block lifts the banner returns to whatever
	// dismissed state it was in, rather than treating the block as a fresh start.
	const blocked = outOfCredits || atRunCap;
	if (dismissed && !blocked) return null;

	const message = (): React.ReactNode => {
		if (outOfCredits) return formatCreditsLong(intl, credits.resetsOn);
		if (atRunCap) return formatMessage({id: 'nova_invocation.control.reason_run_cap'}, {count: RUN_CAP});
		if (running > 0) return formatMessage({id: 'nova_invocation.banner.running'}, {count: running});
		return formatMessage(
			{id: 'nova_invocation.banner.no_runs'},
			{
				action: <ControlRef key="action" kind="action" label={formatMessage({id: 'nova_invocation.control.invoke'})} />,
				arrow: (
					<ControlRef key="arrow" kind="chevron" label={formatMessage({id: 'nova_invocation.banner.arrow_label'})} />
				),
			}
		);
	};

	return (
		<Banner data-cy="nova-entry-banner" role="status" aria-live="polite">
			{/* Dimmed while the account is out of credits: no run can start until the allowance is
			    back. The run limit keeps it lit - a slot frees up as soon as one run finishes. */}
			<Icon
				icon="sparkle"
				size="m"
				color="primary"
				customColor={outOfCredits ? DIMMED_ICON : undefined}
				aria-hidden="true"
				data-cy="nova-entry-banner-icon"
			/>
			<Lines data-cy="nova-entry-banner-text">
				<Label data-cy="nova-entry-banner-heading">{formatMessage({id: 'nova_invocation.banner.heading'})}</Label>
				<Detail color="medium" data-cy="nova-entry-banner-message">
					{message()}
				</Detail>
			</Lines>
			{!blocked && (
				<DismissSlot>
					<Button
						emphasis="low"
						icon="close"
						iconOnly
						useButtonProps
						noMargin
						aria-label={formatMessage({id: 'nova_invocation.banner.dismiss'})}
						data-cy="nova-entry-banner-dismiss"
						onPress={() => {
							writeBannerDismissed(true);
							setDismissed(true);
						}}
					/>
				</DismissSlot>
			)}
		</Banner>
	);
};
